import { useDeferredValue, useMemo } from 'react';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { type InferFilterItemFromFilter } from '@adv-frontend/filters';
import {
    type OpenApiSchemaABCCombinedCategory,
    type OpenApiSchemaCategory,
    type OpenApiSchemaCharacteristicMeta,
    type OpenApiSchemaCollection,
} from '#shared/api/schema';
import { type PaginationOptions, SEARCH_PARAM_KEY_PAGE, useDefaultPagination } from '#shared/components/grid';
import { useGridSearchParams, useSortFromSearchParams } from '#shared/lib/hooks';
import { type ProductVariantSortByKey } from '../api';
import { type ProductGridQueryFilters } from '../lib/types';
import { usePageWithQueryChangeReset } from './usePageWithQueryChangeReset';
import { useProductFilterForBackendRequest } from './useProductFilterForBackendRequest';
import { type ProductFilterDeclaration } from './useProductFiltersDeclaration';
import { useProductVariantsAndCardsQuery } from './useProductVariantsAndCardsQuery';

const SORT_KEYS = new Set([
    'rating',
    'created',
    'final_price_and_price',
    'sort_weight',
    'name',
    'updated',
    'total_min_max_quantity',
]);
const isSortByKey = (key: string): key is ProductVariantSortByKey => SORT_KEYS.has(key);

export type ForceFilters = {
    includeCollectionSlugs?: OpenApiSchemaCollection['slug'][];
    excludeCollectionSlugs?: OpenApiSchemaCollection['slug'][];
    similarCardId?: string;
    excludeSimilarCardId?: string;
    excludeProductCardIds?: string[];
    product_list_id?: string;
    exclude_product_list_id?: string;
    filterItems?: InferFilterItemFromFilter<ProductFilterDeclaration>[];
    categorySlugs?: OpenApiSchemaCategory['slug'][];
    abcCategories?: OpenApiSchemaABCCombinedCategory[];
    skipUrlDynamicFilters?: boolean;
};

export const useProductVariantsAndCardsContentGridProps = (
    characteristics: OpenApiSchemaCharacteristicMeta[] | undefined,
    filters: ProductGridQueryFilters,
    forceFilters?: ForceFilters,
    forceRefetch?: boolean,
    paginationOptions?: PaginationOptions
) => {
    const { searchParams, setSearchParams } = useGridSearchParams();

    const { page, perPage, setPage } = useDefaultPagination(
        searchParams,
        setSearchParams,
        undefined,
        undefined,
        paginationOptions
    );

    const { sortBy, sortOrder, setSort } = useSortFromSearchParams(searchParams, setSearchParams, {
        resetParams: [SEARCH_PARAM_KEY_PAGE],
    });

    const filterItems = useProductFilterForBackendRequest(characteristics, forceFilters?.filterItems, {
        skipUrlDynamicFilters: forceFilters?.skipUrlDynamicFilters,
    });
    // Категории и коллекции записываются в параметры сразу — им нужен debounce; поиск и динамические фильтры уже отложены.
    const debounceTimeout = 300;

    /**
     * Делаем JSON.stringify для того, чтобы можно было использовать в кач-ве зависимости query
     * и избежать лишних перерендеров от новой ссылки массива
     */
    const filter = useMemo(() => JSON.stringify(filterItems), [filterItems]);

    const { categorySlugIds, collectionSlugs, searchString, statuses, moderationStatus } = filters;

    /**
     * Дебаунсим строку, а не массив: `useDebouncedValue` сравнивает значение по ссылке,
     * поэтому новый массив на каждом рендере запускает бесконечный цикл setState по таймеру дебаунса
     */
    const categorySlugIdsDebounced = useDebouncedValue(categorySlugIds ? categorySlugIds : '', debounceTimeout);
    const collectionSlugsDebounced = useDebouncedValue(collectionSlugs, debounceTimeout);

    const pageIndependentQuery = {
        statuses: statuses ?? [],
        moderationStatus,
        searchText: searchString ?? '',
        perPage,
        sortBy,
        sortOrder,
        filter,
        categorySlugIds: categorySlugIdsDebounced,
        collectionSlugs: collectionSlugsDebounced,
        ...forceFilters,
    };

    const pageForQuery = usePageWithQueryChangeReset({
        page,
        pageIndependentQuery,
        setPage,
        isLocalPagination: paginationOptions?.withoutSearchParams === true,
    });

    const deferredValues = useDeferredValue({
        ...pageIndependentQuery,
        page: pageForQuery,
    });

    const query = useProductVariantsAndCardsQuery({
        ...deferredValues,
        isSortByKey,
        forceRefetch,
    });

    return {
        deferredValues,
        setSort,
        setPage,
        query,
    };
};
